import {
  buildTerrain,
  WORLDS,
  dmFuel,
  FUEL,
  MAX_ANGLE,
  MIN_ANGLE,
  MAX_WIND,
  settleY,
  TANK_CENTER_DY,
  walk,
  WEAPON_IDS,
  WEAPONS,
  ammoOf,
  EMOTES,
  hashInts,
  mulberry32,
  WATER_Y,
  WORLD_W,
  GuidedFlight,
  KamikazeFlight,
  BomberFlight,
  BOMBER_BOMBS,
  type EmoteId,
  type TurnInput,
  type GameState,
  type GameView,
  type ServerMsg,
  type TournamentInfo,
  type Terrain,
  type TurnEvents,
  type WeaponId,
} from '@tanky/shared';
import { api } from '../api';
import { formatRemaining, serverNow } from '../clock';
import { h, toast } from '../dom';
import { go, session, type Screen } from '../session';
import { socket } from '../ws';
import { Camera } from './camera';
import { Effects } from './effects';
import { cloneTanks, Playback, type DisplayTank } from './playback';
import { drawScene, drawBomberOpticOverlay, type Scene } from './renderer';
import { TerrainGfx } from './terrainGfx';
import { Leaves } from './leaves';
import { sound } from '../sound';
import { soundButton } from '../ui/soundButton';
import { notificationButton, notifyMyTurn } from '../notifications';
import { chatPanel, type ChatPanel } from '../ui/chatPanel';
import type { Bubble, EmoteState } from './renderer';
import type { GameStatsResponse } from '@tanky/shared';
import { GuidedPilot, KamikazePilot, BomberPilot } from './guidedControl';
import type { TrainingSession } from './training';
import { trainingPanel } from '../ui/trainingPanel';
import { standingsTable } from '../ui/standings';
import type { View3D } from './view3d';
import { loadQuality, QUALITY_LABEL, QUALITY_ORDER, saveQuality } from './three/quality';
import { DRONE_BATTERY, DroneFlight, type DroneEnd, type DroneInput } from './drone';

const VIEW3D_KEY = 'tanky.view3d';
const FAV_WEAPONS_KEY = 'tanky.fav_weapons';
const WEAPON_SORT_KEY = 'tanky.weapon_sort';
const WEAPON_FILTER_FAV_KEY = 'tanky.weapon_filter_fav';

interface QueuedTurn {
  seq: number;
  events: TurnEvents;
  /** Autoritativní stav po tahu (u historických tahů ho neznáme – dopočítá se). */
  final: GameState | null;
}

interface Aim {
  angle: number;
  power: number;
  weapon: WeaponId;
  move: number;
}

type Mode = 'mine' | 'pilot' | 'drone' | 'waiting' | 'sending' | 'replay' | 'finished' | 'spectate';

const seenKey = (id: string) => `tanky.seen.${id}`;
const DIRT_COLOR = { hills: '#8a5a3b', caves: '#6e6284', moon: '#8d8d93', mars: '#a0522d' } as const;

function clamp(v: number, a: number, b: number) {
  return Math.max(a, Math.min(b, v));
}

const activeHoldStops = new Set<() => void>();

function stopAllHolds() {
  for (const stop of activeHoldStops) stop();
  activeHoldStops.clear();
}

/** Tlačítko, které při podržení opakuje akci a postupně zrychluje. */
function holdButton(label: string, title: string, step: (mult: number) => void): HTMLButtonElement {
  const b = h('button', { type: 'button', title, 'aria-label': title }, label);
  let timer: number | undefined;
  let count = 0;
  const stop = () => {
    clearTimeout(timer);
    timer = undefined;
    activeHoldStops.delete(stop);
  };
  const tick = () => {
    if (!b.isConnected) {
      stop();
      return;
    }
    count++;
    step(count > 25 ? 5 : count > 10 ? 2 : 1);
    timer = window.setTimeout(tick, 60);
  };
  b.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    b.setPointerCapture(e.pointerId);
    count = 0;
    step(1);
    stop();
    activeHoldStops.add(stop);
    timer = window.setTimeout(tick, 320);
  });
  b.addEventListener('pointerup', stop);
  b.addEventListener('pointercancel', stop);
  b.addEventListener('lostpointercapture', stop);
  b.addEventListener('contextmenu', (e) => e.preventDefault());
  return b;
}

/** Klávesy letu dronem (průzkumný i kamikadze). */
const DRONE_KEYS = ['w', 's', 'a', 'd', 'q', 'e', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

/** Virtuální páčka (dron): hodnoty x, y v rozsahu −1…1, po puštění se vrátí na střed. */
function stick(label: string, onChange: (x: number, y: number) => void): HTMLElement {
  const knob = h('div', { class: 'knob' });
  const el = h('div', { class: 'stick', title: label }, knob, h('small', {}, label));
  let active = -1;
  const set = (x: number, y: number) => {
    knob.style.transform = `translate(${x * 30}px, ${y * 30}px)`;
    onChange(x, y);
  };
  const move = (e: PointerEvent) => {
    const r = el.getBoundingClientRect();
    const dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
    const dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
    const len = Math.hypot(dx, dy);
    const k = len > 1 ? 1 / len : 1;
    set(dx * k, dy * k);
  };
  el.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    el.setPointerCapture(e.pointerId);
    active = e.pointerId;
    move(e);
  });
  el.addEventListener('pointermove', (e) => e.pointerId === active && move(e));
  const stop = () => {
    active = -1;
    set(0, 0);
  };
  el.addEventListener('pointerup', stop);
  el.addEventListener('pointercancel', stop);
  el.addEventListener('lostpointercapture', stop);
  return el;
}

export class GameScreen implements Screen {
  readonly el: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;

  private view: GameView;
  private state: GameState;
  private latest: GameState;
  private terrain!: Terrain;
  private gfx!: TerrainGfx;
  private gfxStyle: GameState['style'] | null = null;
  private cam = new Camera();
  private fx = new Effects();

  private queue: QueuedTurn[] = [];
  private current: QueuedTurn | null = null;
  private playback: Playback | null = null;
  private queuedSeq = 0;
  private ready = false;
  private pending: ServerMsg[] = [];
  private catchingUp = false;

  private aim: Aim = { angle: 45, power: 60, weapon: 'shell', move: 0 };
  private aimTurn = -1;
  private preview: { x: number; y: number } | null = null;
  private remoteAim: (Aim & { playerId: string; x: number; y: number }) | null = null;
  private lastAimSent = 0;
  private aimSendTimer: number | undefined;
  private sending = false;
  private online = new Set<string>();

  private raf = 0;
  private lastFrame = 0;
  private time = 0;
  private destroyed = false;
  private cleanups: (() => void)[] = [];

  // HUD
  private hudWho = h('div', { class: 'who' });
  private hudTimer = h('div', { class: 'timer' });
  private hudWind = h('div', { class: 'wind pill' });
  private strip = h('div', { class: 'players-strip' });
  private bottom = h('div', { class: 'hud-bottom' });
  private layer = h('div');
  private chat: ChatPanel;
  private chatBox: HTMLElement;
  private chatBadge = h('span', { class: 'badge-dot', hidden: true });
  private chatOpen = false;
  private unread = 0;
  private emotes = new Map<string, EmoteState>();
  private bubbles = new Map<string, Bubble>();
  private lastEmoteSent = 0;
  private windsockX: number;
  private leaves: Leaves;
  private summary: GameStatsResponse | null = null;
  private summaryLoading = false;
  private mode: Mode | null = null;
  private refs: {
    angle?: HTMLElement;
    power?: HTMLElement;
    fuel?: HTMLElement;
    weapon?: HTMLButtonElement;
    fire?: HTMLButtonElement;
    waitText?: HTMLElement;
  } = {};
  private pickerOpen = false;
  private menuOpen = false;
  private overlayDismissed = false;
  /** Odpočet do dalšího kola turnaje v závěrečném okně. */
  private nextRoundEl: HTMLElement | null = null;
  private stripKey = '';
  /** Lokální tréninkový režim (bez serveru). */
  private training: TrainingSession | null;
  private trainPanel: ReturnType<typeof trainingPanel> | null = null;
  private trainBox: HTMLElement | null = null;
  private placeMode = false;
  private trainingResetTimer = 0;
  /** 3D pohled z kabiny (načítá se líně, až při prvním zapnutí). */
  private view3d: View3D | null = null;
  private view3dLoading = false;
  /** Deathmatch: starší přehrávání, která běží souběžně s nejnovějším. */
  private bgPlaybacks: Playback[] = [];
  /** Deathmatch: přehrávání se překrývala → po doběhnutí přestavět terén ze stavu serveru. */
  private dmOverlap = false;
  private driveTimer = 0;
  /** Vlastní let průzkumným dronem. */
  private drone: DroneFlight | null = null;
  /** 3D bylo zapnuté jen kvůli dronu – po přistání ho zase vypneme. */
  private droneRestore2d = false;
  private droneSentAt = 0;
  private droneKeys = new Set<string>();
  private droneSticks = { left: { x: 0, y: 0 }, right: { x: 0, y: 0 } };
  private droneVoice: ReturnType<typeof sound.drone> = null;
  private remoteDroneVoices = new Map<string, ReturnType<typeof sound.drone>>();
  /** Drony ostatních hráčů (živě přes WS). */
  private remoteDrones = new Map<string, { x: number; y: number; z: number; yaw: number; at: number }>();
  private view3dBtn = h('button', { class: 'hud-btn', title: '3D pohled z kabiny', onclick: () => this.toggle3d() }, '👁');
  /** Právě řízená střela (střelec). */
  private pilot: GuidedPilot | KamikazePilot | BomberPilot | null = null;
  /** 3D se zapnulo jen kvůli letu kamikadze dronu. */
  private kamiRestore2d = false;
  /** 3D se zapnulo jen kvůli letu bombardéru. */
  private bomberRestore2d = false;
  /** Kdo naposledy řídil let, který jsme viděli živě – jeho přehrání se přeskočí. */
  private liveFlight: { playerId: string; at: number } | null = null;
  private stopEngine: (() => void) | null = null;
  /** Zvuk motoru řízené střely (posouvá se se střelou). */
  private engineVoice: ReturnType<typeof sound.engineLoop> = null;
  private lastGuideSent = 0;
  /** Řízená střela jiného hráče (poloha z WS). */
  private remoteMissile: { x: number; y: number; z: number; yaw: number | undefined; a: number; until: number; trail: number[]; kamikaze: boolean } | null = null;

  constructor(view: GameView, training: TrainingSession | null = null) {
    this.view = view;
    this.training = training;
    this.state = view.state!;
    this.latest = view.state!;
    this.queuedSeq = view.turnSeq;

    this.windsockX = pickWindsockX(this.state);
    this.leaves = new Leaves(this.state.style);
    this.chat = chatPanel(view.id, {
      canWrite: () => this.state.tanks.some((t) => t.id === this.me),
      allowBubble: () => !!this.myTank()?.alive,
    });
    this.chat.colorOf = (id) => this.state.tanks.find((t) => t.id === id)?.color ?? '#ffb347';
    this.chat.setMessages(view.chat ?? []);
    this.chatBox = h(
      'div',
      { class: 'chat-box', hidden: true },
      h('div', { class: 'chat-head' }, h('b', {}, '💬 Chat'), h('button', { class: 'ghost', type: 'button', onclick: () => this.toggleChat(false) }, '✕')),
      this.chat.el,
    );

    this.canvas = h('canvas');
    this.ctx = this.canvas.getContext('2d')!;
    this.el = h(
      'div',
      { class: 'game' },
      this.canvas,
      h(
        'div',
        { class: 'hud-top' },
        h('button', { class: 'hud-btn', title: 'Zpět do lobby', onclick: () => go('/') }, '←'),
        h('div', { class: 'turn-info pill' }, this.hudWho, this.hudTimer),
        this.hudWind,
        soundButton('hud-btn', this.cleanups),
        notificationButton('hud-btn', this.cleanups),
        this.view3dBtn,
        training
          ? h('button', { class: 'hud-btn', title: 'Nastavení tréninku', onclick: () => this.toggleTrainPanel() }, '⚙')
          : h('button', { class: 'hud-btn chat-btn', title: 'Chat', onclick: () => this.toggleChat() }, '💬', this.chatBadge),
        h('button', { class: 'hud-btn', title: 'Menu', onclick: () => this.toggleMenu() }, '⋯'),
      ),
      this.strip,
      this.bottom,
      this.chatBox,
      this.layer,
    );

    this.setTerrain(this.state);
    this.bindInput();

    if (training) {
      this.trainPanel = trainingPanel(training, {
        run: (change, terrainChanged) => this.trainingChange(change, terrainChanged),
        setPlaceMode: (on) => {
          this.placeMode = on;
          // Cíle se umisťují klepnutím do 2D mapy
          if (on && this.view3d) this.toggle3d(false);
          if (on) toast('Klepni na mapu – přidáš cíl. Klepnutím na cíl ho odebereš.');
        },
        placeMode: () => this.placeMode,
        hide: () => {
          if (this.trainBox) this.trainBox.hidden = true;
        },
      });
      this.trainBox = h('div', { class: 'train-box', hidden: matchMedia('(max-height: 520px)').matches }, this.trainPanel.el);
      this.el.append(this.trainBox);
    } else {
      this.cleanups.push(socket.on((m) => (this.ready ? this.onMessage(m) : this.pending.push(m))));
      this.cleanups.push(socket.onOpen(() => this.ready && this.resync()));
      socket.setGame(view.id);
    }

    const ro = new ResizeObserver(() => this.resize());
    ro.observe(this.el);
    this.cleanups.push(() => ro.disconnect());
    const roundTick = window.setInterval(() => this.tickNextRound(), 500);
    this.cleanups.push(() => clearInterval(roundTick));

    this.raf = requestAnimationFrame((t) => this.frame(t));
    this.init();
    try {
      if (localStorage.getItem(VIEW3D_KEY) === '1') this.toggle3d(true);
    } catch {
      /* úložiště nedostupné */
    }
  }

  destroy() {
    stopAllHolds();
    this.destroyed = true;
    this.view3d?.dispose();
    this.view3d = null;
    cancelAnimationFrame(this.raf);
    clearTimeout(this.aimSendTimer);
    clearTimeout(this.driveTimer);
    clearTimeout(this.trainingResetTimer);
    this.stopEngine?.();
    this.droneVoice?.stop();
    for (const v of this.remoteDroneVoices.values()) v?.stop();
    for (const c of this.cleanups) c();
  }

  // ---------------- Data ----------------

  private get me() {
    return session.me!.id;
  }

  private setTerrain(state: GameState, forceNewGfx = false) {
    this.terrain = buildTerrain(state.seed, state.style, state.ops);
    // Jiný styl mapy (trénink: kopce ↔ jeskyně) nebo vynucená obnova potřebuje novou grafiku
    if (!forceNewGfx && this.gfx && this.gfxStyle === state.style) this.gfx.setTerrain(this.terrain);
    else {
      this.gfx = new TerrainGfx(this.terrain, state.style);
      this.gfxStyle = state.style;
    }
    this.preview = null;
  }

  private async init() {
    if (this.training) {
      this.ready = true;
      this.updateHud(true);
      return;
    }
    const v = this.view;
    const stored = localStorage.getItem(seenKey(v.id));
    const isPlayer = this.state.tanks.some((t) => t.id === this.me);
    const since = this.state.dm
      ? v.turnSeq
      : stored !== null
        ? Number(stored)
        : isPlayer
          ? Math.max(0, v.turnSeq - this.state.tanks.length)
          : v.turnSeq;
    if (since < v.turnSeq) {
      try {
        const r = await api.turns(v.id, since);
        if (r.stateBefore && r.turns.length) {
          this.state = r.stateBefore;
          this.setTerrain(this.state);
          r.turns.forEach((t, i) =>
            this.queue.push({ seq: t.seq, events: t.events, final: i === r.turns.length - 1 ? v.state : null }),
          );
          this.catchingUp = this.queue.length > 1;
        }
      } catch {
        /* bez přehrání – zobrazí se aktuální stav */
      }
    }
    if (this.destroyed) return;
    this.ready = true;
    for (const m of this.pending.splice(0)) this.onMessage(m);
    this.updateHud(true);
  }

  private onMessage(m: ServerMsg) {
    if (m.t === 'turn' && m.gameId === this.view.id) {
      this.view.deadline = m.deadline;
      if (m.seq <= this.queuedSeq) return;
      if (m.seq === this.queuedSeq + 1) {
        this.queue.push({ seq: m.seq, events: m.events, final: m.state });
        this.queuedSeq = m.seq;
        this.latest = m.state;
        this.view.turnSeq = m.seq;
      } else {
        this.resync();
      }
    } else if (m.t === 'aim' && m.gameId === this.view.id) {
      const cur = this.latest.tanks[this.latest.current];
      if (!cur || cur.id !== m.playerId || m.playerId === this.me) return;
      const tank = this.state.tanks.find((t) => t.id === m.playerId);
      if (!tank) return;
      const move = clamp(m.move, -FUEL, FUEL);
      const w = move ? walk(this.terrain, tank.x, tank.y, move < 0 ? -1 : 1, Math.abs(move)) : tank;
      this.remoteAim = { ...m, move, x: w.x, y: w.y };
    } else if (m.t === 'guide' && m.gameId === this.view.id && m.playerId !== this.me) {
      const rm = this.remoteMissile;
      const trail = rm && this.time < rm.until ? rm.trail : [];
      trail.push(m.x, m.y);
      if (trail.length > 40) trail.splice(0, trail.length - 40);
      this.remoteMissile = { x: m.x, y: m.y, z: m.z ?? 0, yaw: m.yaw, a: m.a, until: this.time + 0.6, trail, kamikaze: !!m.k };
      this.liveFlight = { playerId: m.playerId, at: this.time };
    } else if (m.t === 'drone' && m.gameId === this.view.id && m.playerId !== this.me) {
      if (m.done) this.dropRemoteDrone(m.playerId);
      else {
        this.remoteDrones.set(m.playerId, { x: m.x, y: m.y, z: m.z, yaw: m.yaw, at: this.time });
        const v = this.remoteDroneVoices.get(m.playerId);
        if (v) v.setPosition(m.x, m.y, m.z);
        else this.remoteDroneVoices.set(m.playerId, sound.drone(m.x, m.y, m.z));
      }
    } else if (m.t === 'presence' && m.gameId === this.view.id) {
      this.online = new Set(m.online);
      this.stripKey = '';
    } else if (m.t === 'chat' && m.gameId === this.view.id) {
      this.chat.add(m.msg);
      if (m.msg.bubble) this.bubbles.set(m.msg.userId, { text: m.msg.text, start: this.time, until: this.time + 5.5 });
      if (m.msg.userId !== this.me) sound.chat();
      if (!this.chatOpen && m.msg.userId !== this.me) {
        this.unread++;
        this.chatBadge.hidden = false;
        this.chatBadge.textContent = String(Math.min(this.unread, 99));
      }
    } else if (m.t === 'emote' && m.gameId === this.view.id) {
      this.playEmote(m.playerId, m.kind);
    } else if (m.t === 'game' && m.gameId === this.view.id) {
      this.refreshView();
    }
  }

  private async refreshView() {
    try {
      const v = (await api.game(this.view.id)).game;
      this.view = { ...v, turnSeq: this.view.turnSeq };
      if (v.turnSeq > this.queuedSeq) this.resync();
      this.renderLayer();
    } catch {
      /* ignorovat */
    }
  }

  /** Dotáhne tahy, které nám utekly (výpadek WS apod.). */
  private async resync() {
    try {
      const [{ game }, turns] = await Promise.all([api.game(this.view.id), api.turns(this.view.id, this.queuedSeq)]);
      if (this.destroyed || !game.state) return;
      if (game.state.dm && game.turnSeq > this.queuedSeq) {
        this.view = game;
        this.latest = game.state;
        this.skipReplay(game.state, game.turnSeq);
        this.queuedSeq = game.turnSeq;
        return;
      }
      if (game.turnSeq <= this.queuedSeq) {
        this.view = { ...game, turnSeq: this.queuedSeq };
        return;
      }
      const contiguous = turns.turns.length > 0 && turns.turns[0].seq === this.queuedSeq + 1;
      if (contiguous) {
        const last = turns.turns[turns.turns.length - 1].seq;
        for (const t of turns.turns) {
          this.queue.push({ seq: t.seq, events: t.events, final: t.seq === last && last === game.turnSeq ? game.state : null });
        }
        this.queuedSeq = last;
      } else {
        this.skipReplay(game.state, game.turnSeq);
      }
      this.view = game;
      this.latest = game.state;
      this.queuedSeq = Math.max(this.queuedSeq, game.turnSeq);
    } catch {
      /* další pokus při dalším připojení */
    }
  }

  private skipReplay(state = this.latest, seq = this.queuedSeq) {
    this.queue = [];
    this.playback = null;
    this.bgPlaybacks = [];
    this.current = null;
    this.catchingUp = false;
    this.state = state;
    this.fx = new Effects();
    this.setTerrain(state);
    localStorage.setItem(seenKey(this.view.id), String(seq));
    this.updateHud(true);
  }

  private finishPlayback() {
    const pb = this.playback!;
    const item = this.current!;
    if (this.state.dm) {
      // Stav se v deathmatchi převzal už při startu přehrávání
      if (!pb.opsInOrder) this.dmOverlap = true;
      this.playback = null;
      this.current = null;
      localStorage.setItem(seenKey(this.view.id), String(item.seq));
      this.updateHud(false);
      return;
    }
    const expectedOps = this.state.ops.length + item.events.ops.length;
    let next: GameState;
    if (item.final) {
      next = item.final;
    } else {
      next = structuredClone(this.state);
      for (const t of next.tanks) {
        const d = pb.tanks.find((x) => x.id === t.id)!;
        Object.assign(t, { x: d.x, y: d.y, hp: d.hp, alive: d.alive, angle: d.angle, power: d.power });
      }
      for (const { op } of item.events.ops) next.ops.push(op);
    }
    this.state = next;
    if (!pb.opsInOrder || next.ops.length !== expectedOps) this.setTerrain(next);
    this.preview = null;
    this.remoteAim = null;
    this.playback = null;
    this.current = null;
    this.aim.move = 0;
    if (this.training) {
      this.trainPanel?.refresh();
      this.scheduleTrainingAutoReset();
    } else localStorage.setItem(seenKey(this.view.id), String(item.seq));
    if (!this.queue.length) this.catchingUp = false;
    this.updateHud(true);
  }

  private isMyTurn() {
    const s = this.state;
    if (s.dm) {
      // Deathmatch: „na tahu“ je každý živý hráč, pořád
      return this.ready && !this.sending && !this.pilot && s.status === 'playing' && !!this.myTank()?.alive;
    }
    return (
      this.ready &&
      !this.playback &&
      !this.queue.length &&
      !this.sending &&
      !this.pilot &&
      s.status === 'playing' &&
      s.tanks[s.current]?.id === this.me
    );
  }

  private myTank() {
    return this.state.tanks.find((t) => t.id === this.me);
  }

  // ---------------- Míření ----------------

  private ensureAim() {
    if (this.state.dm) {
      // Míření se v deathmatchi drží napříč akcemi, nastaví se jen poprvé
      const t = this.myTank();
      if (this.aimTurn === -2 || !t) return;
      this.aimTurn = -2;
      this.aim = { angle: t.angle, power: t.power, weapon: 'shell', move: 0 };
      return;
    }
    if (this.aimTurn === this.state.turn) return;
    const tank = this.state.tanks[this.state.current];
    this.aimTurn = this.state.turn;
    const weapon = ammoOf(tank.ammo, this.aim.weapon) !== 0 ? this.aim.weapon : 'shell';
    this.aim = { angle: tank.angle, power: tank.power, weapon, move: 0 };
    this.preview = null;
    // Zaostřit na vlastní tank, pokud je přiblíženo
    if (this.cam.zoom > this.cam.fitZoom() * 1.05) {
      this.cam.x = tank.x;
      this.cam.y = tank.y - 60;
      this.cam.clamp();
    }
  }

  private setAim(patch: Partial<Aim>) {
    if (!this.isMyTurn()) return;
    const a = this.aim;
    if (patch.angle !== undefined) a.angle = Math.round(clamp(patch.angle, MIN_ANGLE, MAX_ANGLE) * 10) / 10;
    if (patch.power !== undefined) a.power = Math.round(clamp(patch.power, 0, 100) * 10) / 10;
    if (patch.weapon !== undefined) a.weapon = patch.weapon;
    if (patch.move !== undefined && patch.move !== a.move) {
      const tank = this.myTank()!;
      tank.y = settleY(this.terrain, tank.x, tank.y);
      const maxMove = this.state.dm ? dmFuel(tank, serverNow()) : FUEL;
      const m = clamp(Math.round(patch.move), -maxMove, maxMove);
      const w = m ? walk(this.terrain, tank.x, tank.y, m < 0 ? -1 : 1, Math.abs(m)) : { x: tank.x, y: tank.y, path: [] };
      // Zeď nás zastavila → nespotřebovat palivo navíc
      a.move = m ? Math.sign(m) * (w.path.length / 2) : 0;
      this.preview = a.move !== 0 ? { x: w.x, y: w.y } : null;
      if (this.state.dm) this.scheduleDrive();
    }
    this.updateControls();
    this.sendAim();
  }

  private sendAim() {
    if (this.training) return;
    const now = performance.now();
    const doSend = () => {
      this.lastAimSent = performance.now();
      const { angle, power, move, weapon } = this.aim;
      socket.send({ t: 'aim', gameId: this.view.id, angle, power, move, weapon });
    };
    clearTimeout(this.aimSendTimer);
    if (now - this.lastAimSent > 120) doSend();
    else this.aimSendTimer = window.setTimeout(doSend, 120);
  }

  /** Deathmatch: po chvíli bez výstřelu se popojetí odešle samo. */
  private scheduleDrive() {
    clearTimeout(this.driveTimer);
    this.driveTimer = window.setTimeout(async () => {
      const move = this.aim.move;
      if (!move || this.sending || this.destroyed) return;
      const tank = this.myTank();
      if (!tank) return;
      tank.y = settleY(this.terrain, tank.x, tank.y);
      const w = walk(this.terrain, tank.x, tank.y, move < 0 ? -1 : 1, Math.abs(move));
      tank.x = w.x;
      tank.y = w.y;
      tank.fuel = Math.max(0, dmFuel(tank, serverNow()) - Math.abs(move));
      tank.fuelAt = serverNow();
      this.aim.move = 0;
      this.preview = null;
      this.updateControls();
      try {
        await api.drive(this.view.id, move);
      } catch (e) {
        toast((e as Error).message, true);
        this.resync();
      }
      this.updateControls();
    }, 180);
  }

  private async fire() {
    if (!this.isMyTurn()) return;
    this.pickerOpen = false;
    stopAllHolds();
    clearTimeout(this.aimSendTimer);
    clearTimeout(this.driveTimer);

    if (this.state.dm) {
      const t = this.myTank()!;
      const left = (t.reloadAt ?? 0) - serverNow();
      if (left > 0) return toast(`Nabíjím… ${Math.ceil(left / 1000)} s`);
      if (ammoOf(t.ammo, this.aim.weapon) === 0) {
        toast('Tahle zbraň došla – vyber jinou.');
        return this.cycleWeapon();
      }
      const input = { ...this.aim };
      this.aim.move = 0;
      this.preview = null;
      await this.commit(input);
      this.updateControls();
      return;
    }
    if (this.aim.weapon === 'guided' || this.aim.weapon === 'kamikaze' || this.aim.weapon === 'bomber') {
      this.startPilot();
      return;
    }
    const input = { ...this.aim };
    this.aim.move = 0;
    this.preview = null;
    this.updateControls();
    await this.commit(input);
  }

  /** Spustí živé řízení řízené střely (tah se odešle až po dopadu). */
  private startPilot() {
    const me = this.myTank()!;
    const pos = this.preview ?? { x: me.x, y: me.y };
    const tanks = this.state.tanks.map((t) => (t.id === this.me ? { ...t, x: pos.x, y: pos.y } : t));
    const kami = this.aim.weapon === 'kamikaze';
    const bomber = this.aim.weapon === 'bomber';
    const shooter = { id: this.me, x: pos.x, y: pos.y };
    if (bomber) {
      this.pilot = new BomberPilot(new BomberFlight(this.aim.angle), this.terrain, this.state.wind);
      // Pohled z bombardéru shora – 3D se zapne jen na dobu náletu
      if (!this.view3d) {
        this.bomberRestore2d = true;
        void this.toggle3d(true, false);
      }
      toast('🛩️ Bombardér: MEZERNÍK / KLIK = shodit bomby! Zamiř křížem na zemi.');
    } else if (kami) {
      this.pilot = new KamikazePilot(new KamikazeFlight(this.terrain, tanks, shooter, this.aim.angle, this.state.wind));
    } else {
      this.pilot = new GuidedPilot(new GuidedFlight(this.terrain, tanks, shooter, this.aim.angle));
    }
    if (kami) {
      this.droneKeys.clear();
      this.droneSticks = { left: { x: 0, y: 0 }, right: { x: 0, y: 0 } };
      // Pohled z kamery dronu (FPV) – 3D se zapne jen na dobu letu
      if (!this.view3d) {
        this.kamiRestore2d = true;
        void this.toggle3d(true, false);
      }
      toast('Kamikadze dron: W/S výška, A/D otočení, šipky pohyb, mezerník odpálit. Na mobilu páčky.');
    }
    const engine = kami ? sound.drone(pos.x, pos.y, 0) : sound.engineLoop(pos.x, pos.y);
    this.engineVoice = engine;
    this.stopEngine = () => {
      engine?.stop();
      this.engineVoice = null;
    };
    sound.fire(bomber ? 'bomber' : kami ? 'kamikaze' : 'guided', pos.x);
    this.updateHud(true);
  }

  private updatePilot(dt: number) {
    const p = this.pilot!;
    const kami = p instanceof KamikazePilot ? p : null;
    const bomber = p instanceof BomberPilot ? p : null;
    if (kami) Object.assign(kami.input, this.droneInput());
    if (bomber) {
      bomber.update(dt, (bx: number) => {
        sound.whistle(bx);
      });
      if (!this.view3d) {
        this.cam.follow(bomber.flight.x, Math.max(120, bomber.crosshair.y - 140), dt * 2.5);
      }
    } else {
      p.update(dt);
      const f = p.flight;
      this.cam.follow(f.x, f.y, dt * 1.5);
    }
    const f = p.flight;
    this.engineVoice?.setPosition(f.x, f.y, kami?.flight.z ?? 0);
    if (this.refs.fuel) {
      if (bomber) this.refs.fuel.style.width = `${(bomber.flight.bombsLeft / BOMBER_BOMBS) * 100}%`;
      else if ('fuel' in f) this.refs.fuel.style.width = `${f.fuel * 100}%`;
    }
    if (!bomber && !this.training && performance.now() - this.lastGuideSent > 100) {
      this.lastGuideSent = performance.now();
      socket.send({
        t: 'guide',
        gameId: this.view.id,
        x: f.x,
        y: f.y,
        a: Math.atan2(f.vy, f.vx),
        ...(kami ? { k: true, z: kami.flight.z, yaw: kami.flight.yaw } : {}),
      });
    }
    if (p.done) {
      this.liveFlight = { playerId: this.me, at: this.time };
      this.stopEngine?.();
      this.stopEngine = null;
      if (this.kamiRestore2d) {
        this.kamiRestore2d = false;
        void this.toggle3d(false, false);
      }
      if (this.bomberRestore2d) {
        this.bomberRestore2d = false;
        void this.toggle3d(false, false);
      }
      const steer = p.steer;
      this.pilot = null;
      const input = { ...this.aim, steer };
      this.aim.move = 0;
      this.preview = null;
      this.updateControls();
      this.commit(input);
    }
  }

  /** Přehrávání tahu; let, který jsme právě viděli živě, se přeskočí rovnou k výbuchu. */
  private newPlayback(events: TurnEvents): Playback {
    const pb = new Playback(events, this.state, this.gfx, this.fx, DIRT_COLOR[this.state.style]);
    const live = this.liveFlight;
    if (live && live.playerId === events.playerId && this.time - live.at < 10 && pb.skipFlight()) this.liveFlight = null;
    return pb;
  }

  /** Odešle tah – na server, nebo v tréninku lokálně. */
  private async commit(input: TurnInput) {
    if (this.training) {
      clearTimeout(this.trainingResetTimer);
      try {
        const r = this.training.fire(input);
        this.queuedSeq++;
        this.queue.push({ seq: this.queuedSeq, events: r.events, final: r.state });
        this.latest = r.state;
      } catch (e) {
        toast((e as Error).message, true);
      }
      this.updateHud(true);
      return;
    }
    this.sending = true;
    this.updateHud(true);
    const expected = this.queuedSeq + 1;
    try {
      await api.turn(this.view.id, input);
      // Pokud WS zprávu nedoručí, dotáhneme tah sami
      setTimeout(() => {
        if (!this.destroyed && this.queuedSeq < expected) this.resync();
      }, 1500);
    } catch (e) {
      toast((e as Error).message, true);
      this.resync();
    } finally {
      this.sending = false;
      this.updateHud(true);
    }
  }

  // ---------------- Vstup ----------------

  private bindInput() {
    const c = this.canvas;
    const pointers = new Map<number, { x: number; y: number }>();
    let mode: 'none' | 'aim' | 'pan' | 'pinch' = 'none';
    let pinch = { dist: 0, mx: 0, my: 0 };

    const pos = (e: PointerEvent) => {
      const r = c.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const pinchInfo = () => {
      const [a, b] = [...pointers.values()];
      return { dist: Math.hypot(a.x - b.x, a.y - b.y), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
    };
    const aimAt = (p: { x: number; y: number }) => {
      const t = this.displayMine();
      if (!t) return;
      const origin = this.cam.toScreen(t.x, t.y - TANK_CENTER_DY);
      const dx = p.x - origin.x;
      const dy = origin.y - p.y;
      let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
      // Šikmo dolů vlevo: místo -150° chceme 210°
      if (angle < -90) angle += 360;
      const reach = Math.min(this.cam.vw, this.cam.vh) * 0.42;
      this.setAim({ angle, power: (Math.hypot(dx, dy) / reach) * 100 });
    };

    let downAt: { x: number; y: number } | null = null;
    c.addEventListener('pointerdown', (e) => {
      if (this.pilot instanceof BomberPilot) {
        (this.pilot as BomberPilot).dropBomb();
        return;
      }
      c.setPointerCapture(e.pointerId);
      const p = pos(e);
      downAt = pointers.size === 0 ? p : null;
      pointers.set(e.pointerId, p);
      this.pickerOpen = false;
      this.menuOpen = false;
      this.renderLayer();
      if (pointers.size === 1) {
        const t = this.isMyTurn() ? this.displayMine() : null;
        const near = !this.placeMode && t && (() => {
          const s = this.cam.toScreen(t.x, t.y - TANK_CENTER_DY);
          return Math.hypot(s.x - p.x, s.y - p.y) < 70;
        })();
        mode = near || e.button === 2 ? 'aim' : 'pan';
        if (mode === 'aim' && e.button === 2) aimAt(p);
      } else if (pointers.size === 2) {
        mode = 'pinch';
        pinch = pinchInfo();
      }
    });
    c.addEventListener('pointermove', (e) => {
      const prev = pointers.get(e.pointerId);
      if (!prev) return;
      const p = pos(e);
      pointers.set(e.pointerId, p);
      if (mode === 'aim') aimAt(p);
      else if (mode === 'pan') {
        this.cam.pan(p.x - prev.x, p.y - prev.y);
        this.cam.manualAt = this.time;
      } else if (mode === 'pinch' && pointers.size === 2) {
        const now = pinchInfo();
        if (pinch.dist > 0) this.cam.zoomAt(now.mx, now.my, now.dist / pinch.dist);
        this.cam.pan(now.mx - pinch.mx, now.my - pinch.my);
        this.cam.manualAt = this.time;
        pinch = now;
      }
    });
    const up = (e: PointerEvent) => {
      const p = pos(e);
      if (this.training && this.placeMode && downAt && pointers.size === 1 && Math.hypot(p.x - downAt.x, p.y - downAt.y) < 8) {
        const w = this.cam.toWorld(p.x, p.y);
        this.trainingChange(() => {
          const r = this.training!.toggleTargetAt(w.x, w.y);
          if (r === 'full') toast('Víc cílů už se nevejde.');
        }, false);
        this.trainPanel?.refresh();
      }
      pointers.delete(e.pointerId);
      if (pointers.size === 0) mode = 'none';
      else if (mode === 'pinch') mode = 'pan';
    };
    c.addEventListener('pointerup', up);
    c.addEventListener('pointercancel', up);
    c.addEventListener('contextmenu', (e) => e.preventDefault());
    c.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        const r = c.getBoundingClientRect();
        this.cam.zoomAt(e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY * 0.0015));
        this.cam.manualAt = this.time;
      },
      { passive: false },
    );

    const steerKey = (e: KeyboardEvent, down: boolean) => {
      if (!this.pilot) return false;
      const k = e.key;
      const p = this.pilot;
      if (p instanceof BomberPilot) {
        if (k === ' ' || k === 'Enter' || k === 'b' || k === 'B') {
          p.setDrop(down);
          e.preventDefault();
          return true;
        }
        return false;
      }
      if (p instanceof KamikazePilot) {
        // Stejné klávesy jako průzkumný dron, mezerník / Enter = odpálit
        const key = k.length === 1 ? k.toLowerCase() : k;
        if (key === ' ' || key === 'Enter') {
          if (down) p.detonate();
        } else if (DRONE_KEYS.includes(key)) {
          if (down) this.droneKeys.add(key);
          else this.droneKeys.delete(key);
        } else return false;
        e.preventDefault();
        return true;
      }
      if (k === 'ArrowLeft' || k === 'a' || k === 'A') p.setKeyDir(down ? -1 : p.dir === -1 ? 0 : p.dir);
      else if (k === 'ArrowRight' || k === 'd' || k === 'D') p.setKeyDir(down ? 1 : p.dir === 1 ? 0 : p.dir);
      else return false;
      e.preventDefault();
      return true;
    };
    const droneKey = (e: KeyboardEvent, down: boolean) => {
      if (!this.drone || (e.target as HTMLElement)?.tagName === 'INPUT') return false;
      if (down && (e.key === 'Escape' || e.key === 'Enter')) {
        this.endDrone('return');
      } else {
        const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
        if (!DRONE_KEYS.includes(k)) return false;
        if (down) this.droneKeys.add(k);
        else this.droneKeys.delete(k);
      }
      e.preventDefault();
      return true;
    };
    const onKeyUp = (e: KeyboardEvent) => droneKey(e, false) || steerKey(e, false);
    window.addEventListener('keyup', onKeyUp);
    this.cleanups.push(() => window.removeEventListener('keyup', onKeyUp));

    const onKey = (e: KeyboardEvent) => {
      if (droneKey(e, true) || steerKey(e, true) || this.drone) return;
      // Při psaní do chatu nesmí klávesy ovládat tank
      if (!this.isMyTurn() || this.chatOpen || (e.target as HTMLElement)?.tagName === 'INPUT') return;
      const fine = e.shiftKey ? 0.2 : 1;
      const a = this.aim;
      switch (e.key) {
        case 'ArrowLeft':
          this.setAim({ angle: a.angle + fine });
          break;
        case 'ArrowRight':
          this.setAim({ angle: a.angle - fine });
          break;
        case 'ArrowUp':
          this.setAim({ power: a.power + fine });
          break;
        case 'ArrowDown':
          this.setAim({ power: a.power - fine });
          break;
        case 'a':
        case 'A':
          this.setAim({ move: a.move - 2 });
          break;
        case 'd':
        case 'D':
          this.setAim({ move: a.move + 2 });
          break;
        case 'Tab':
        case 'w':
        case 'W':
          this.cycleWeapon();
          break;
        case ' ':
        case 'Enter':
          if (!e.repeat) this.fire();
          break;
        default:
          if (e.key >= '0' && e.key <= '9') {
            // 1–9 a 0 = prvních 10 zbraní podle aktuálního pořadí (oblíbené na začátku!)
            const ordered = this.getOrderedWeapons(false);
            const id = ordered[(Number(e.key) + 9) % 10];
            if (id && ammoOf(this.myTank()!.ammo, id) !== 0) this.setAim({ weapon: id });
          } else return;
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    this.cleanups.push(() => window.removeEventListener('keydown', onKey));
  }

  private getFavorites(): Set<WeaponId> {
    try {
      const raw = localStorage.getItem(FAV_WEAPONS_KEY);
      if (raw) {
        const list = JSON.parse(raw);
        if (Array.isArray(list)) return new Set(list.filter((x): x is WeaponId => x in WEAPONS));
      }
    } catch {}
    return new Set<WeaponId>();
  }

  private toggleFavorite(id: WeaponId) {
    const favs = this.getFavorites();
    if (favs.has(id)) favs.delete(id);
    else favs.add(id);
    localStorage.setItem(FAV_WEAPONS_KEY, JSON.stringify([...favs]));
    this.renderLayer();
  }

  private isFavorite(id: WeaponId): boolean {
    return this.getFavorites().has(id);
  }

  private getWeaponSort(): 'alpha' | 'default' {
    return (localStorage.getItem(WEAPON_SORT_KEY) as 'alpha' | 'default') || 'alpha';
  }

  private setWeaponSort(sort: 'alpha' | 'default') {
    localStorage.setItem(WEAPON_SORT_KEY, sort);
    this.renderLayer();
  }

  private getOnlyFavorites(): boolean {
    return localStorage.getItem(WEAPON_FILTER_FAV_KEY) === '1';
  }

  private toggleOnlyFavorites() {
    const next = !this.getOnlyFavorites();
    localStorage.setItem(WEAPON_FILTER_FAV_KEY, next ? '1' : '0');
    this.renderLayer();
  }

  /** Seznam zbraní s respektováním oblíbených a řazení (výchozí abecední). */
  private getOrderedWeapons(filterOnlyFavs = false): WeaponId[] {
    const favSet = this.getFavorites();
    const sort = this.getWeaponSort();

    let favs = WEAPON_IDS.filter((id) => favSet.has(id));
    let rest = WEAPON_IDS.filter((id) => !favSet.has(id));

    if (sort === 'alpha') {
      favs.sort((a, b) => WEAPONS[a].name.localeCompare(WEAPONS[b].name, 'cs'));
      rest.sort((a, b) => WEAPONS[a].name.localeCompare(WEAPONS[b].name, 'cs'));
    }

    if (filterOnlyFavs) {
      return favs;
    }
    return [...favs, ...rest];
  }

  private cycleWeapon() {
    const tank = this.myTank()!;
    const ordered = this.getOrderedWeapons(false);
    const i = ordered.indexOf(this.aim.weapon);
    for (let k = 1; k <= ordered.length; k++) {
      const id = ordered[(i + k) % ordered.length];
      if (ammoOf(tank.ammo, id) !== 0) {
        this.setAim({ weapon: id });
        return;
      }
    }
  }

  // ---------------- Smyčka ----------------

  private resize() {
    const w = this.el.clientWidth;
    const hh = this.el.clientHeight;
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(hh * this.dpr);
    this.cam.resize(w, hh);
    this.view3d?.resize(w, hh, this.dpr);
  }

  private frame(now: number) {
    if (this.destroyed) return;
    this.raf = requestAnimationFrame((t) => this.frame(t));
    const dt = Math.min(0.05, this.lastFrame ? (now - this.lastFrame) / 1000 : 0);
    this.lastFrame = now;
    this.time += dt;

    if (this.ready && this.state.dm && this.queue.length) {
      // Deathmatch: každá akce se přehraje hned, i když předchozí ještě běží
      while (this.queue.length) {
        const item = this.queue.shift()!;
        if (item.events.kind === 'drive' && item.events.playerId === this.me) {
          if (item.final) this.state = item.final;
          localStorage.setItem(seenKey(this.view.id), String(item.seq));
          continue;
        }
        if (this.playback) {
          this.bgPlaybacks.push(this.playback);
          this.dmOverlap = true;
        }
        this.current = item;
        this.playback = this.newPlayback(item.events);
        if (item.final) this.state = item.final;
        if (item.events.playerId === this.me) this.preview = null;
      }
      // Nestrukturální obnova – překreslení ovládání by přerušilo držená tlačítka
      this.updateHud(false);
    }
    if (this.bgPlaybacks.length) {
      for (const pb of this.bgPlaybacks) pb.update(dt);
      this.bgPlaybacks = this.bgPlaybacks.filter((pb) => !pb.done);
    }
    if (this.ready && !this.playback && this.queue.length) {
      this.current = this.queue.shift()!;
      this.playback = this.newPlayback(this.current.events);
      this.updateHud(true);
    }
    if (this.playback) {
      this.playback.update(dt * (this.catchingUp ? 2 : 1));
      const f = this.playback.focus();
      if (f && this.time - this.cam.manualAt > 2) this.cam.follow(f.x, f.y - 40, dt);
      if (this.playback.done) this.finishPlayback();
    }
    if (this.pilot) this.updatePilot(dt);
    if (this.drone) this.updateDrone(dt);
    if (this.state.dm) {
      // Terén po souběžných výbuších srovnat se serverem, až bude klid
      if (this.dmOverlap && !this.playback && !this.bgPlaybacks.length) {
        this.dmOverlap = false;
        this.setTerrain(this.state);
      }
      for (const t of this.state.tanks) {
        if (t.alive) t.y = settleY(this.terrain, t.x, t.y);
      }
      if (Math.floor(this.time * 10) !== Math.floor((this.time - dt) * 10)) this.updateControls();
    }
    for (const [id, d] of this.remoteDrones) if (this.time - d.at > 2) this.dropRemoteDrone(id);
    if (this.isMyTurn()) this.ensureAim();
    this.fx.update(dt);
    this.leaves.update(dt, this.cam.vw, this.cam.vh, this.state.wind, this.cam);
    const myTank = this.myTank();
    sound.setEar(myTank?.alive ? { x: myTank.x, y: myTank.y } : null);
    if (this.view3d) sound.setListener3D(this.view3d.listenerPose());
    else sound.setListener(this.cam.x, this.cam.y, this.cam.vw / 2 / this.cam.zoom);

    const tanks = this.displayTanks();
    // Kouř z vraků
    if (Math.random() < dt * 3) {
      const dead = tanks.filter((t) => !t.alive && t.y < 868);
      if (dead.length) {
        const t = dead[Math.floor(Math.random() * dead.length)];
        this.fx.smoke(t.x, t.y - 8);
      }
    }

    const cur = this.state.status === 'playing' && !this.playback ? this.state.tanks[this.state.current] : null;
    let aim: Scene['aim'] = null;
    if (this.isMyTurn()) {
      const t = tanks.find((x) => x.id === this.me)!;
      aim = { tank: t, angle: this.aim.angle, power: this.aim.power, mine: true };
    } else if (!this.playback && this.remoteAim && cur && cur.id === this.remoteAim.playerId) {
      const t = tanks.find((x) => x.id === cur.id)!;
      aim = { tank: t, angle: this.remoteAim.angle, power: this.remoteAim.power, mine: false };
    }

    if (this.view3d) {
      this.view3d.render(
        {
          terrain: this.terrain,
          gfx: this.gfx,
          style: this.state.style,
          tanks,
          focusId: this.focusTankId(tanks),
          aim: aim ? { angle: aim.angle, power: aim.power } : null,
          projectiles: this.kamiCamera() ? this.extraProjectiles().filter((p) => p.weapon !== 'kamikaze') : this.extraProjectiles(),
          fx: this.fx,
          mines: this.playback ? this.playback.mines : this.state.mines ?? [],
          camera: this.drone
            ? { x: this.drone.x, y: this.drone.y, z: this.drone.z, yaw: this.drone.yaw, pitch: this.drone.pitch, roll: this.drone.roll }
            : this.kamiCamera(),
          drones: this.droneList(tanks).map((d) => ({ ...d, z: d.z ?? 0, yaw: d.yaw ?? 0 })),
          bomber:
            this.pilot instanceof BomberPilot && !this.pilot.flightDone
              ? {
                  x: this.pilot.flight.x,
                  y: this.pilot.flight.y,
                  targetX: this.pilot.crosshair.x,
                  targetY: this.pilot.crosshair.y,
                  dir: this.pilot.flight.dir,
                }
              : null,
        },
        dt,
      );
      if (this.pilot instanceof BomberPilot && !this.pilot.flightDone) {
        this.canvas.style.zIndex = '1';
        this.canvas.style.pointerEvents = 'none';
        this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        this.ctx.clearRect(0, 0, this.cam.vw, this.cam.vh);
        drawBomberOpticOverlay(
          this.ctx,
          this.cam.vw,
          this.cam.vh,
          this.pilot.flight.bombsLeft,
          this.state.wind,
          this.time,
        );
      } else if (this.canvas.style.zIndex === '1') {
        this.canvas.style.zIndex = '';
        this.canvas.style.pointerEvents = '';
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      }
    } else {
      if (this.canvas.style.zIndex === '1') {
        this.canvas.style.zIndex = '';
        this.canvas.style.pointerEvents = '';
      }
      drawScene(this.ctx, this.cam, this.dpr, {
        style: this.state.style,
        terrain: this.gfx.canvas,
        tanks,
        currentId: this.playback ? this.playback.events.playerId : cur?.id ?? null,
        meId: this.me,
        projectiles: this.extraProjectiles(),
        effects: this.fx,
        aim,
        time: this.time,
        emotes: this.emotes,
        bubbles: this.bubbles,
        windsock: this.windsockPos(),
        mines: this.playback ? this.playback.mines : this.state.mines ?? [],
        wind: this.state.wind,
        leaves: this.leaves,
        drones: this.droneList(tanks),
        bombsight:
          this.pilot instanceof BomberPilot && !this.pilot.flightDone
            ? {
                x: this.pilot.crosshair.x,
                y: this.pilot.crosshair.y,
                planeX: this.pilot.flight.x,
                planeY: this.pilot.flight.y,
                bombsLeft: this.pilot.flight.bombsLeft,
              }
            : null,
      });
    }

    if (Math.floor(this.time * 4) !== Math.floor((this.time - dt) * 4)) this.updateHud(false);
  }

  /** Z kabiny kterého tanku se ve 3D díváme: vlastní živý tank, jinak ten, kdo hraje. */
  private focusTankId(tanks: DisplayTank[]): string | null {
    const mine = tanks.find((t) => t.id === this.me && t.alive);
    if (mine) return mine.id;
    if (this.playback) return this.playback.events.playerId;
    return this.state.status === 'playing' ? this.state.tanks[this.state.current]?.id ?? null : null;
  }

  /** Přepne 3D pohled (Three.js se načte až při prvním zapnutí). */
  private async toggle3d(on = !this.view3d, remember = true) {
    if (on === !!this.view3d || this.view3dLoading) return;
    // Během letu dronem zůstává 3D zapnuté
    if (!on && this.drone) return;
    if (remember) {
      this.droneRestore2d = false;
      try {
        localStorage.setItem(VIEW3D_KEY, on ? '1' : '0');
      } catch {
        /* nevadí */
      }
    }
    this.view3dBtn.classList.toggle('active', on);
    if (!on) {
      this.view3d!.dispose();
      this.view3d = null;
      return;
    }
    this.view3dLoading = true;
    try {
      const { View3D } = await import('./view3d');
      if (this.destroyed) return;
      const v = new View3D(loadQuality());
      v.onQualityDrop = (level) => toast(`3D se sekalo – kvalita snížena na ${QUALITY_LABEL[level].toLowerCase()}.`);
      this.canvas.after(v.canvas);
      this.bind3dInput(v.canvas);
      this.view3d = v;
      this.resize();
    } catch (e) {
      this.view3dBtn.classList.remove('active');
      const msg = (e as Error).message;
      if (msg.includes('dynamically imported module') || msg.includes('Failed to fetch')) {
        toast('Byla nasazena nová verze – obnovuji stránku...');
        setTimeout(() => window.location.reload(), 1000);
        return;
      }
      toast(`3D pohled nejde spustit: ${msg}`, true);
    } finally {
      this.view3dLoading = false;
    }
  }

  /** Míření ve 3D: tažení nahoru/dolů zvedá a sklápí hlaveň, doleva/doprava mění sílu. */
  private bind3dInput(c: HTMLCanvasElement) {
    let start: { x: number; y: number; angle: number; power: number } | null = null;
    c.addEventListener('pointerdown', (e) => {
      if (this.pilot instanceof BomberPilot) {
        (this.pilot as BomberPilot).dropBomb();
        return;
      }
      if (!this.isMyTurn()) return;
      c.setPointerCapture(e.pointerId);
      start = { x: e.clientX, y: e.clientY, angle: this.aim.angle, power: this.aim.power };
    });
    c.addEventListener('pointermove', (e) => {
      if (!start) return;
      const dy = start.y - e.clientY;
      const dx = e.clientX - start.x;
      // Nahoru = hlaveň k svislici, na obou stranách
      const raise = start.angle <= 90 ? 1 : -1;
      this.setAim({ angle: start.angle + dy * 0.25 * raise, power: start.power + dx * 0.2 });
    });
    const end = () => (start = null);
    c.addEventListener('pointerup', end);
    c.addEventListener('pointercancel', end);
  }

  private dropRemoteDrone(id: string) {
    this.remoteDrones.delete(id);
    this.remoteDroneVoices.get(id)?.stop();
    this.remoteDroneVoices.delete(id);
  }

  private droneKey() {
    return `tanky.drone.${this.view.id}`;
  }

  /** Dron jde vypustit, když jsi na tahu a v tomto tahu jsi ještě neletěl (v tréninku vždy). */
  private droneAvailable(): boolean {
    if (this.drone || this.mode !== 'mine') return false;
    if (this.training) return true;
    try {
      if (this.state.dm) return serverNow() - Number(localStorage.getItem(this.droneKey()) ?? 0) > 30_000;
      return localStorage.getItem(this.droneKey()) !== String(this.state.turn);
    } catch {
      return true;
    }
  }

  private async startDrone() {
    if (!this.droneAvailable()) return;
    const tank = this.myTank();
    if (!tank) return;
    if (!this.view3d) {
      await this.toggle3d(true, false);
      if (!this.view3d) return;
      this.droneRestore2d = true;
    }
    if (!this.training) {
      try {
        localStorage.setItem(this.droneKey(), String(this.state.dm ? serverNow() : this.state.turn));
      } catch {
        /* nevadí */
      }
    }
    this.droneKeys.clear();
    this.droneSticks = { left: { x: 0, y: 0 }, right: { x: 0, y: 0 } };
    this.drone = new DroneFlight(this.terrain, tank.x, tank.y - TANK_CENTER_DY, this.aim.angle <= 90, this.state.wind);
    this.droneVoice = sound.drone(this.drone.x, this.drone.y, this.drone.z);
    toast('Dron: W/S výška, A/D otočení, šipky pohyb, Esc návrat. Na mobilu páčky.');
    this.updateHud(true);
  }

  private updateDrone(dt: number) {
    const d = this.drone!;
    // Tah skončil (vypršel čas…) → dron přistane
    if (!this.training && !this.isMyTurn()) return this.endDrone('return');
    Object.assign(d.input, this.droneInput());
    d.update(dt);
    this.droneVoice?.setPosition(d.x, d.y, d.z);
    if (this.refs.fuel) this.refs.fuel.style.width = `${(d.battery / DRONE_BATTERY) * 100}%`;
    if (!this.training && this.time - this.droneSentAt > 0.1) {
      this.droneSentAt = this.time;
      socket.send({ t: 'drone', gameId: this.view.id, x: d.x, y: d.y, z: d.z, yaw: d.yaw });
    }
    if (d.ended) this.endDrone(d.ended);
  }

  /** Vstup dronu (průzkumného i kamikadze) z kláves a páček. */
  private droneInput(): DroneInput {
    const k = this.droneKeys;
    const key = (a: string, b: string) => (k.has(a) ? 1 : 0) - (k.has(b) ? 1 : 0);
    const { left, right } = this.droneSticks;
    const clamp1 = (v: number) => Math.max(-1, Math.min(1, v));
    return {
      throttle: clamp1(key('w', 's') - left.y),
      yaw: clamp1(key('d', 'a') + left.x),
      forward: clamp1(key('ArrowUp', 'ArrowDown') - right.y),
      strafe: clamp1(key('ArrowRight', 'ArrowLeft') + key('e', 'q') + right.x),
    };
  }

  private endDrone(reason: DroneEnd) {
    const d = this.drone;
    if (!d) return;
    this.drone = null;
    this.droneVoice?.stop();
    this.droneVoice = null;
    if (!this.training) socket.send({ t: 'drone', gameId: this.view.id, x: d.x, y: d.y, z: d.z, yaw: d.yaw, done: true });
    if (reason === 'crash') {
      this.fx.explosion(d.x, d.y, 8);
      toast('💥 Dron narazil do terénu.');
    } else if (reason === 'water') {
      this.fx.splash(d.x);
      toast('🌊 Dron spadl do vody.');
    } else if (reason === 'battery') toast('🔋 Dronu došla baterie.');
    if (this.droneRestore2d) {
      this.droneRestore2d = false;
      this.toggle3d(false, false);
    }
    this.updateHud(true);
  }

  /** Drony ve vzduchu pro vykreslení (vlastní jen, když se na něj nekoukáme zevnitř). */
  private droneList(tanks: DisplayTank[]) {
    const colorOf = (id: string) => tanks.find((t) => t.id === id)?.color ?? '#ffb347';
    const list: { id: string; x: number; y: number; z?: number; yaw?: number; color: string }[] = [];
    for (const [id, d] of this.remoteDrones) list.push({ id, ...d, color: colorOf(id) });
    // Letící kamikadze drony (přehrávání, soupeř) – ne vlastní, z jehož kamery se díváme
    if (this.view3d && !this.kamiCamera()) {
      this.extraProjectiles()
        .filter((p) => p.weapon === 'kamikaze')
        .forEach((p, i) => list.push({ id: `kami${i}`, x: p.x, y: p.y, z: p.z ?? 0, yaw: p.yaw ?? (p.vx < 0 ? Math.PI : 0), color: '#e74c3c' }));
    }
    if (this.drone && !this.view3d) list.push({ id: this.me, x: this.drone.x, y: this.drone.y, color: colorOf(this.me) });
    return list;
  }

  private windsockPos() {
    // Na Měsíci není vítr – anemoskop tam nemá smysl
    if (WORLDS[this.state.style].wind === 0) return null;
    const y = this.terrain.surfaceY(this.windsockX);
    if (y >= WATER_Y - 2) return null;
    return { x: this.windsockX, y, wind: this.state.wind };
  }

  private displayTanks(): DisplayTank[] {
    if (this.state.dm) {
      const tanks = cloneTanks(this.state.tanks);
      const activePlaybacks = [this.playback, ...this.bgPlaybacks].filter((pb): pb is Playback => !!pb);
      for (const pb of activePlaybacks) {
        const pbShooter = pb.shooter;
        if (pbShooter) {
          const shooter = tanks.find((t) => t.id === pbShooter.id);
          if (shooter) {
            shooter.x = pbShooter.x;
            shooter.y = pbShooter.y;
            shooter.angle = pbShooter.angle;
            shooter.recoil = pbShooter.recoil;
          }
        }
        for (const pt of pb.tanks) {
          const t = tanks.find((x) => x.id === pt.id);
          if (!t) continue;
          if (pt.recoil) t.recoil = pt.recoil;
          if (pb.isFalling(pt.id) || pb.isPulled(pt.id)) {
            t.x = pt.x;
            t.y = pt.y;
          }
        }
      }
      const me = tanks.find((t) => t.id === this.me);
      if (me?.alive && this.isMyTurn()) {
        if (this.preview) {
          me.x = this.preview.x;
          me.y = this.preview.y;
        }
        me.angle = this.aim.angle;
        me.power = this.aim.power;
      }
      for (const t of tanks) {
        const isFalling = activePlaybacks.some((pb) => pb.isFalling(t.id));
        if (t.alive && !isFalling) {
          t.y = settleY(this.terrain, t.x, t.y);
        }
      }
      return tanks;
    }
    if (this.playback) return this.playback.tanks;
    const tanks = cloneTanks(this.state.tanks);
    const cur = this.state.status === 'playing' ? tanks[this.state.current] : undefined;
    if (!cur) return tanks;
    if (this.isMyTurn() && cur.id === this.me) {
      if (this.preview) {
        cur.x = this.preview.x;
        cur.y = this.preview.y;
      }
      cur.angle = this.aim.angle;
      cur.power = this.aim.power;
    } else if (this.remoteAim && this.remoteAim.playerId === cur.id) {
      cur.x = this.remoteAim.x;
      cur.y = this.remoteAim.y;
      cur.angle = this.remoteAim.angle;
      cur.power = this.remoteAim.power;
    }
    return tanks;
  }

  private displayMine(): DisplayTank | undefined {
    return this.displayTanks().find((t) => t.id === this.me);
  }

  // ---------------- HUD ----------------

  private currentMode(): Mode {
    if (this.pilot) return 'pilot';
    if (this.drone) return 'drone';
    if (this.state.dm) {
      if (!this.ready) return 'waiting';
      if (this.state.status === 'finished') return this.playback || this.bgPlaybacks.length ? 'waiting' : 'finished';
      return this.myTank()?.alive ? 'mine' : 'spectate';
    }
    if (!this.ready || this.playback || this.queue.length) return this.catchingUp ? 'replay' : 'waiting';
    if (this.sending) return 'sending';
    if (this.state.status === 'finished') return 'finished';
    if (this.isMyTurn()) return 'mine';
    const me = this.myTank();
    return me?.alive ? 'waiting' : 'spectate';
  }

  private updateHud(structural: boolean) {
    const s = this.state;
    const pbPlayer = this.playback ? s.tanks.find((t) => t.id === this.playback!.events.playerId) : null;
    const cur = pbPlayer ?? (s.status === 'playing' ? s.tanks[s.current] : null);

    // Kdo hraje
    if (s.dm && s.status === 'playing') {
      const alive = !!this.myTank()?.alive;
      const kills = s.dm.scores[this.me]?.kills ?? 0;
      this.hudWho.replaceChildren(h('span', {}, alive ? `💥 Deathmatch · ☠ ${kills}` : this.myTank() ? '☠ Vyřazen – sleduješ' : '💥 Deathmatch'));
      const remaining = s.dm.endsAt - serverNow();
      this.hudTimer.textContent = `⏱ ${formatRemaining(remaining)}`;
      this.hudTimer.classList.toggle('urgent', remaining < 30000);
    } else if (s.status === 'finished' && !this.playback) {
      const w = s.tanks.find((t) => t.id === s.winner);
      this.hudWho.replaceChildren(h('span', {}, w ? `🏆 Vyhrál ${w.name}` : 'Remíza'));
      this.hudTimer.textContent = 'Konec hry';
      this.hudTimer.classList.remove('urgent');
    } else if (cur) {
      const mine = cur.id === this.me;
      this.hudWho.replaceChildren(
        h('span', { class: 'dot', style: { background: cur.color } }),
        h('span', {}, this.playback ? `Tah: ${cur.name}` : mine ? 'Jsi na tahu!' : `Hraje ${cur.name}`),
      );
      const remaining = this.view.deadline ? this.view.deadline - serverNow() : 0;
      if (this.playback || this.queue.length || !this.view.deadline) {
        this.hudTimer.textContent = this.catchingUp ? 'přehrávám zmeškané tahy' : '';
        this.hudTimer.classList.remove('urgent');
      } else {
        this.hudTimer.textContent = `⏱ ${formatRemaining(remaining)}`;
        this.hudTimer.classList.toggle('urgent', remaining < 15000);
      }
    }

    const tour = this.view.tournament;
    if (tour) {
      const round = `🏆 ${tour.round}/${tour.rounds}`;
      this.hudTimer.textContent = this.hudTimer.textContent ? `${this.hudTimer.textContent} · ${round}` : round;
    }

    if (this.training) {
      this.hudTimer.textContent = this.placeMode ? '📍 klepni na mapu = cíl' : this.pilot ? this.pilotHint() : this.drone ? '🛸 průzkum dronem' : '🎯 Trénink · ∞ munice';
      this.hudTimer.classList.remove('urgent');
    } else if (this.pilot) {
      this.hudTimer.textContent = this.pilotHint();
    }

    // Vítr
    const wind = this.state.wind;
    const k = Math.abs(wind) / MAX_WIND;
    const dir = Math.sign(wind);
    const len = 6 + k * 22;
    this.hudWind.innerHTML = `<span class="small muted">vítr</span><svg viewBox="-30 -7 60 14" aria-hidden="true">${
      wind === 0
        ? '<circle r="3" fill="#9aa4c0"/>'
        : `<path d="M${-dir * len} 0 H${dir * len} M${dir * (len - 6)} -5 L${dir * len} 0 L${dir * (len - 6)} 5" stroke="${k > 0.6 ? '#ff8a5c' : '#cfe3ff'}" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`
    }</svg><b class="small">${Math.abs(wind)}</b>`;

    this.renderStrip();

    const mode = this.currentMode();
    if (structural || mode !== this.mode) {
      // Upozornění na tah a změnu stavu
      if (mode === 'mine' && !this.state.dm) {
        if (this.mode && mode !== this.mode) {
          notifyMyTurn({ gameId: this.view.id, inCurrentGame: true });
        } else if (!this.mode && typeof document !== 'undefined' && (document.hidden || !document.hasFocus())) {
          notifyMyTurn({ gameId: this.view.id, inCurrentGame: true });
        }
      }
      if (this.mode && mode !== this.mode && mode === 'finished' && this.state.winner === this.me) {
        sound.win();
      }
      this.mode = mode;
      this.renderBottom();
      this.renderLayer();
    } else if (this.refs.waitText && mode === 'waiting' && cur) {
      this.refs.waitText.textContent = this.waitingText(cur.name);
    }
  }

  private renderStrip() {
    const tanks = this.playback ? this.playback.tanks : this.state.tanks;
    const curId = this.playback ? this.playback.events.playerId : this.state.status === 'playing' ? this.state.tanks[this.state.current]?.id : null;
    const scores = this.state.dm?.scores;
    const key = JSON.stringify([tanks.map((t) => [t.hp, t.alive]), curId, [...this.online], scores]);
    if (key === this.stripKey) return;
    this.stripKey = key;
    this.strip.replaceChildren(
      ...tanks.map((t) =>
        h(
          'div',
          { class: `p${t.alive ? '' : ' dead'}${t.id === curId ? ' cur' : ''}`, title: this.online.has(t.id) ? 'online' : 'offline' },
          h('span', { class: 'dot', style: { background: t.color, boxShadow: this.online.has(t.id) ? `0 0 0 2px rgba(76,209,138,.7)` : 'none' } }),
          h('span', {}, t.name),
          h('span', { class: 'hp' }, h('i', { style: { width: `${Math.max(0, t.hp)}%` } })),
          scores ? h('span', { class: 'kills', title: 'Zabití' }, `☠${scores[t.id]?.kills ?? 0}`) : null,
        ),
      ),
    );
  }

  private waitingText(name: string) {
    if (this.playback) return `${name} hraje…`;
    return `Čeká se na tah hráče ${name}`;
  }

  private renderBottom() {
    stopAllHolds();
    this.refs = {};
    const mode = this.mode!;
    if (mode === 'mine') {
      this.ensureAim();
      const angle = h('div', { class: 'val' });
      const power = h('div', { class: 'val' });
      const fuel = h('i');
      const weapon = h('button', { class: 'weapon-btn', type: 'button', onclick: () => this.togglePicker() });
      const fire = h('button', { class: 'fire-btn', type: 'button', onclick: () => this.fire() }, 'PAL!');
      this.refs = { angle, power, fuel, weapon, fire };
      this.bottom.replaceChildren(
        h(
          'div',
          { class: 'ctrl' },
          holdButton('◀', 'Jet doleva', (m) => this.setAim({ move: this.aim.move - m })),
          h('div', { class: 'val' }, h('small', {}, 'palivo'), h('div', { class: 'fuel' }, fuel)),
          holdButton('▶', 'Jet doprava', (m) => this.setAim({ move: this.aim.move + m })),
        ),
        weapon,
        h(
          'div',
          { class: 'ctrl' },
          holdButton('↺', 'Zvednout hlaveň doleva', (m) => this.setAim({ angle: this.aim.angle + m })),
          angle,
          holdButton('↻', 'Sklopit hlaveň doprava', (m) => this.setAim({ angle: this.aim.angle - m })),
        ),
        h(
          'div',
          { class: 'ctrl' },
          holdButton('−', 'Menší síla', (m) => this.setAim({ power: this.aim.power - m })),
          power,
          holdButton('+', 'Větší síla', (m) => this.setAim({ power: this.aim.power + m })),
        ),
        fire,
      );
      this.updateControls();
    } else if (mode === 'pilot' && this.pilot instanceof BomberPilot) {
      const p = this.pilot;
      const fuel = h('i', { style: { width: `${(p.flight.bombsLeft / BOMBER_BOMBS) * 100}%` } });
      this.refs = { fuel };
      this.bottom.replaceChildren(
        h(
          'div',
          { class: 'ctrl bomber-ctrl' },
          h('div', { class: 'val' }, h('small', {}, '💣 bomby'), h('b', {}, `${p.flight.bombsLeft} / ${BOMBER_BOMBS}`)),
          h(
            'button',
            {
              type: 'button',
              class: 'bomber-view-btn',
              onclick: () => {
                if (this.view3d) {
                  this.bomberRestore2d = false;
                  void this.toggle3d(false, false);
                } else {
                  this.bomberRestore2d = true;
                  void this.toggle3d(true, false);
                }
                this.updateHud(true);
              },
            },
            this.view3d ? '🗺️ 2D pohled' : '👁 3D optika',
          ),
          h(
            'button',
            {
              type: 'button',
              class: 'fire-btn bomber-drop-btn',
              disabled: p.flight.bombsLeft <= 0,
              onpointerdown: (e: PointerEvent) => {
                e.preventDefault();
                p.setDrop(true);
              },
              onpointerup: () => p.setDrop(false),
              onpointercancel: () => p.setDrop(false),
            },
            '💣 SHODIT BOMBU',
          ),
        ),
      );
    } else if (mode === 'pilot' && this.pilot instanceof KamikazePilot) {
      // Stejné páčky jako průzkumný dron, uprostřed baterie a odpálení
      const fuel = h('i', { style: { width: '100%' } });
      this.refs = { fuel };
      const p = this.pilot;
      this.bottom.replaceChildren(
        h(
          'div',
          { class: 'drone-ctrl' },
          stick('plyn · otočení', (x, y) => (this.droneSticks.left = { x, y })),
          h(
            'div',
            { class: 'ctrl drone-info' },
            h('div', { class: 'val' }, h('small', {}, '🚁 baterie'), h('div', { class: 'fuel' }, fuel)),
            h('button', { type: 'button', class: 'fire-btn', title: 'Odpálit (mezerník)', onclick: () => p.detonate() }, '💥'),
          ),
          stick('dopředu · úkrok', (x, y) => (this.droneSticks.right = { x, y })),
        ),
      );
    } else if (mode === 'pilot') {
      const fuel = h('i', { style: { width: '100%' } });
      this.refs = { fuel };
      const steerBtn = (label: string, title: string, dir: -1 | 1) => {
        const b = h('button', { type: 'button', class: 'steer-btn', title, 'aria-label': title }, label);
        const stop = () => (this.pilot as GuidedPilot | null)?.setTouchDir(0);
        b.addEventListener('pointerdown', (e) => {
          e.preventDefault();
          b.setPointerCapture(e.pointerId);
          (this.pilot as GuidedPilot | null)?.setTouchDir(dir);
        });
        b.addEventListener('pointerup', stop);
        b.addEventListener('pointercancel', stop);
        b.addEventListener('lostpointercapture', stop);
        b.addEventListener('contextmenu', (e) => e.preventDefault());
        return b;
      };
      this.bottom.replaceChildren(
        h(
          'div',
          { class: 'ctrl pilot-ctrl' },
          steerBtn('⟲', 'Zatočit doleva (←)', -1),
          h('div', { class: 'val' }, h('small', {}, '🚀 palivo'), h('div', { class: 'fuel' }, fuel)),
          steerBtn('⟳', 'Zatočit doprava (→)', 1),
        ),
      );
    } else if (mode === 'drone') {
      const fuel = h('i', { style: { width: '100%' } });
      this.refs = { fuel };
      this.bottom.replaceChildren(
        h(
          'div',
          { class: 'drone-ctrl' },
          stick('plyn · otočení', (x, y) => (this.droneSticks.left = { x, y })),
          h(
            'div',
            { class: 'ctrl drone-info' },
            h('div', { class: 'val' }, h('small', {}, '🛸 baterie'), h('div', { class: 'fuel' }, fuel)),
            h('button', { type: 'button', title: 'Ukončit let (Esc)', onclick: () => this.endDrone('return') }, '⏎ Návrat'),
          ),
          stick('dopředu · úkrok', (x, y) => (this.droneSticks.right = { x, y })),
        ),
      );
    } else if (mode === 'finished') {
      this.bottom.replaceChildren();
    } else {
      const s = this.state;
      const cur = s.status === 'playing' ? s.tanks[s.current] : null;
      const text =
        mode === 'sending'
          ? 'Odesílám tah…'
          : mode === 'replay'
            ? 'Přehrávám zmeškané tahy…'
            : mode === 'spectate'
              ? `${this.myTank() ? 'Jsi vyřazen – sleduješ hru' : 'Sleduješ hru'}${cur ? ` · hraje ${cur.name}` : ''}`
              : this.waitingText(cur?.name ?? '…');
      const waitText = h('span', {}, text);
      const canEmote = !this.training && mode === 'waiting' && !!this.myTank()?.alive;
      const emoteBar = canEmote
        ? h(
            'div',
            { class: 'ctrl emotes' },
            ...EMOTES.map((e) => h('button', { type: 'button', title: e.name, 'aria-label': e.name, onclick: () => this.sendEmote(e.id) }, e.icon)),
          )
        : null;
      this.refs.waitText = mode === 'waiting' ? waitText : undefined;
      this.bottom.replaceChildren(
        h(
          'div',
          { class: 'pill waiting row' },
          waitText,
          mode === 'replay' ? h('button', { type: 'button', onclick: () => this.skipReplay() }, 'Přeskočit') : null,
        ),
        ...(emoteBar ? [emoteBar] : []),
      );
    }
  }

  private updateControls() {
    const r = this.refs;
    if (!r.angle) return;
    const a = this.aim;
    // Úhel zobrazujeme jako náklon od vodorovné roviny (0–90°) + směr
    const currentAngle = a.angle.toFixed(1);
    if (r.angle.dataset.val !== currentAngle) {
      r.angle.dataset.val = currentAngle;
      const tilt = a.angle <= 90 ? a.angle : 180 - a.angle;
      r.angle.replaceChildren(h('small', {}, 'úhel'), `${a.angle <= 90 ? '' : '◂ '}${tilt.toFixed(0)}°${a.angle <= 90 ? ' ▸' : ''}`);
    }

    const currentPower = a.power.toFixed(1);
    if (r.power && r.power.dataset.val !== currentPower) {
      r.power.dataset.val = currentPower;
      r.power.replaceChildren(h('small', {}, 'síla'), a.power.toFixed(0));
    }

    const dmTank = this.state.dm ? this.myTank() : undefined;
    const fuelNow = dmTank ? dmFuel(dmTank, serverNow()) : FUEL;
    if (r.fuel) {
      r.fuel.style.width = `${(Math.max(0, fuelNow - Math.abs(a.move)) / FUEL) * 100}%`;
    }
    if (dmTank && r.fire) {
      const left = (dmTank.reloadAt ?? 0) - serverNow();
      r.fire.disabled = left > 0;
      const fireText = left > 0 ? `${(left / 1000).toFixed(1)} s` : 'PAL!';
      if (r.fire.textContent !== fireText) r.fire.textContent = fireText;
    }

    const tank = this.myTank();
    const ammo = tank ? ammoOf(tank.ammo, a.weapon) : 0;
    const weaponKey = `${a.weapon}:${ammo}`;
    if (r.weapon && r.weapon.dataset.key !== weaponKey) {
      r.weapon.dataset.key = weaponKey;
      const w = WEAPONS[a.weapon];
      r.weapon.replaceChildren(
        h('span', { class: 'ico' }, w.icon),
        h('span', {}, w.name, h('small', {}, ammo < 0 ? '∞' : `× ${ammo}`)),
      );
    }
  }

  private toggleChat(open = !this.chatOpen) {
    this.chatOpen = open;
    this.chatBox.hidden = !open;
    if (open) {
      this.unread = 0;
      this.chatBadge.hidden = true;
      this.menuOpen = false;
      this.pickerOpen = false;
      this.renderLayer();
      this.chat.focus();
    }
  }

  private sendEmote(kind: EmoteId) {
    const now = performance.now();
    if (now - this.lastEmoteSent < 1200) return;
    this.lastEmoteSent = now;
    socket.send({ t: 'emote', gameId: this.view.id, kind });
  }

  /** Spustí emote na tanku hráče (od serveru, tj. i vlastní). */
  private playEmote(playerId: string, kind: EmoteId) {
    const t = this.displayTanks().find((x) => x.id === playerId);
    if (!t || !t.alive) return;
    sound.emote(kind, t.x);
    const e = this.emotes.get(playerId) ?? { blinkUntil: 0, hopAt: -Infinity, waveUntil: 0 };
    this.emotes.set(playerId, e);
    switch (kind) {
      case 'blink':
        e.blinkUntil = this.time + 2.4;
        break;
      case 'hop':
        e.hopAt = this.time;
        break;
      case 'wave':
        e.waveUntil = this.time + 2;
        break;
      case 'flare':
        this.fx.flare(t.x, t.y - 14);
        break;
      case 'firework':
        this.fx.firework(t.x, t.y - 14);
        break;
      case 'smoke':
        this.fx.smokeScreen(t.x, t.y);
        break;
    }
  }

  /** Tabulka a ocenění na konci hry (načte se jednou ze serveru). */
  private summaryNode(): Node {
    if (!this.summary) {
      if (!this.summaryLoading) {
        this.summaryLoading = true;
        api
          .gameStats(this.view.id)
          .then((r) => {
            this.summary = r;
            if (!this.destroyed) this.renderLayer();
          })
          .catch(() => (this.summaryLoading = false));
      }
      return h('p', { class: 'muted small', style: { margin: '0' } }, 'Počítám statistiky…');
    }
    const { stats, awards } = this.summary;
    const tanks = this.state.tanks;
    const nameOf = (id: string) => {
      const t = tanks.find((x) => x.id === id);
      return h('a', { href: `#/player/${id}`, style: { color: t?.color ?? 'inherit' } }, t?.name ?? '?');
    };
    const rows = Object.entries(stats).sort((a, b) => (a[1].placement ?? 99) - (b[1].placement ?? 99));
    return h(
      'div',
      { class: 'summary' },
      h(
        'div',
        { class: 'table-wrap' },
        h(
          'table',
          { class: 'stats-table compact' },
          h('thead', {}, h('tr', {}, h('th', {}, '#'), h('th', { class: 'name' }, 'Hráč'), h('th', { title: 'Škody soupeřům' }, 'Škody'), h('th', {}, 'Zabití'), h('th', { title: 'Zásahy / výstřely' }, 'Zásahy'))),
          h(
            'tbody',
            {},
            ...rows.map(([id, s]) =>
              h(
                'tr',
                { class: id === this.me ? 'me' : '' },
                h('td', { class: 'rank' }, s.placement === 1 ? '🥇' : s.placement === 2 ? '🥈' : s.placement === 3 ? '🥉' : String(s.placement ?? '–')),
                h('td', { class: 'name' }, nameOf(id)),
                h('td', {}, String(s.damageDealt)),
                h('td', {}, String(s.kills)),
                h('td', {}, `${s.hits}/${s.shots}`),
              ),
            ),
          ),
        ),
      ),
      awards.length
        ? h(
            'div',
            { class: 'awards' },
            ...awards
              .filter((a) => a.title !== 'Vítěz')
              .map((a) => h('div', { class: 'award', title: a.detail }, h('span', { class: 'ico' }, a.icon), h('b', {}, a.title), nameOf(a.playerId), h('small', { class: 'muted' }, a.detail))),
          )
        : '',
    );
  }

  private pilotHint(): string {
    if (this.pilot instanceof BomberPilot) return '🛩️ MEZERNÍK / KLIK = shodit bomby · zamiř křížem na zemi';
    return this.pilot instanceof KamikazePilot ? '🚁 W/S výška · A/D otočení · šipky pohyb · mezerník = odpálit' : '🚀 řiď šipkami ← →';
  }

  /** Pohled z kamery kamikadze dronu (FPV ve 3D). */
  private kamiCamera() {
    const p = this.pilot;
    if (!(p instanceof KamikazePilot)) return null;
    const f = p.flight;
    return { x: f.x, y: f.y - 3, z: f.z, yaw: f.yaw, pitch: p.pitch, roll: p.roll };
  }

  /** Projektily k vykreslení: přehrávání + právě řízená střela + střela soupeře. */
  private extraProjectiles() {
    const list = this.playback?.projectiles() ?? [];
    for (const pb of this.bgPlaybacks) list.push(...pb.projectiles());
    const p = this.pilot;
    if (p) {
      const f = p.flight;
      const n = p.pts.length;
      const kami = p instanceof KamikazePilot ? p : null;
      const bomber = p instanceof BomberPilot ? p : null;
      if (bomber) {
        list.push({
          x: f.x,
          y: f.y,
          weapon: 'bomberplane',
          bomblet: false,
          trail: p.pts.slice(Math.max(0, n - 30)),
          age: f.age,
          vx: f.vx,
          vy: f.vy,
        });
        for (const b of bomber.bombs) {
          if (!b.alive) continue;
          list.push({
            x: b.x,
            y: b.y,
            weapon: 'bomber',
            bomblet: false,
            trail: b.trail.slice(-30),
            age: 0,
            vx: b.vx,
            vy: b.vy,
          });
        }
      } else {
        list.push({
          x: f.x,
          y: f.y,
          weapon: kami ? 'kamikaze' : 'guided',
          bomblet: false,
          trail: p.pts.slice(Math.max(0, n - 40)),
          age: f.age,
          vx: f.vx,
          vy: f.vy,
          ...(kami ? { z: kami.flight.z, yaw: kami.flight.yaw } : {}),
        });
      }
    }
    const rm = this.remoteMissile;
    if (rm && !this.playback && this.time < rm.until) {
      list.push({
        x: rm.x,
        y: rm.y,
        weapon: rm.kamikaze ? 'kamikaze' : 'guided',
        bomblet: false,
        trail: rm.trail.slice(),
        age: 0,
        vx: Math.cos(rm.a),
        vy: Math.sin(rm.a),
        ...(rm.kamikaze ? { z: rm.z, yaw: rm.yaw } : {}),
      });
    }
    return list;
  }

  /** Změna tréninku z panelu (vítr, cíle, mapa). Během letu střely se nic nemění. */
  private trainingChange(change: () => void, terrainChanged: boolean): boolean {
    if (!this.training) return false;
    if (this.playback || this.queue.length || this.pilot) {
      toast('Počkej, až dopadne střela.');
      return false;
    }
    clearTimeout(this.trainingResetTimer);
    change();
    this.state = this.latest = this.training.state;
    if (terrainChanged) {
      this.setTerrain(this.state, true);
      this.windsockX = pickWindsockX(this.state);
      this.leaves = new Leaves(this.state.style);
      this.fx = new Effects();
      this.aimTurn = -1;
    }
    this.preview = null;
    this.stripKey = '';
    this.updateHud(true);
    return true;
  }

  private scheduleTrainingAutoReset() {
    if (!this.training) return;
    clearTimeout(this.trainingResetTimer);
    const targets = this.state.tanks.filter((t) => t.target);
    const allTargetsDestroyed = targets.length > 0 && targets.every((t) => !t.alive);
    const player = this.myTank();
    const playerDead = !player || !player.alive || player.y >= WATER_Y;
    const hasOps = this.state.ops.length > 0;

    if ((allTargetsDestroyed || playerDead || (targets.length === 0 && hasOps)) && hasOps) {
      const msg = allTargetsDestroyed
        ? '🎯 Všechny cíle zničeny! Obnovuji terén...'
        : playerDead
          ? '💥 Tank zničen! Obnovuji terén...'
          : '🧹 Obnovuji terén...';
      toast(msg);

      this.trainingResetTimer = window.setTimeout(() => {
        if (this.destroyed || !this.training || this.playback || this.queue.length || this.pilot) return;
        this.training.resetTerrain();
        this.state = this.latest = this.training.state;
        this.setTerrain(this.state, true);
        this.windsockX = pickWindsockX(this.state);
        this.leaves = new Leaves(this.state.style);
        this.fx = new Effects();
        this.aimTurn = -1;
        this.preview = null;
        this.stripKey = '';
        this.updateHud(true);
        this.trainPanel?.refresh();
        toast('🧹 Terén byl obnoven.');
      }, 1200);
    }
  }

  private toggleTrainPanel() {
    if (!this.trainBox) return;
    this.trainBox.hidden = !this.trainBox.hidden;
    if (!this.trainBox.hidden) this.trainPanel?.refresh();
  }

  private togglePicker() {
    this.pickerOpen = !this.pickerOpen;
    this.menuOpen = false;
    this.renderLayer();
  }

  private toggleMenu() {
    this.menuOpen = !this.menuOpen;
    this.pickerOpen = false;
    this.renderLayer();
  }

  /** Plovoucí vrstvy: výběr zbraně, menu, konec hry. */
  private renderLayer() {
    const nodes: Node[] = [];
    if (this.pickerOpen && this.mode === 'mine') {
      const tank = this.myTank()!;
      const sort = this.getWeaponSort();
      const onlyFavs = this.getOnlyFavorites();
      const favCount = this.getFavorites().size;
      const displayWeapons = this.getOrderedWeapons(onlyFavs);

      nodes.push(
        h(
          'div',
          { class: 'weapon-picker' },
          h(
            'div',
            { class: 'weapon-picker-header' },
            h('span', { class: 'weapon-picker-title' }, 'Výběr zbraně'),
            h(
              'div',
              { class: 'weapon-picker-tools' },
              h(
                'button',
                {
                  type: 'button',
                  class: `weapon-picker-btn ${sort === 'alpha' ? 'active' : ''}`,
                  title: sort === 'alpha' ? 'Řazeno podle abecedy (klikni pro výchozí herní pořadí)' : 'Výchozí pořadí (klikni pro abecední řazení)',
                  onclick: (e: MouseEvent) => {
                    e.stopPropagation();
                    this.setWeaponSort(sort === 'alpha' ? 'default' : 'alpha');
                  },
                },
                sort === 'alpha' ? '🔤 Abecedně' : '⚡ Výchozí',
              ),
              h(
                'button',
                {
                  type: 'button',
                  class: `weapon-picker-btn ${onlyFavs ? 'active' : ''}`,
                  title: onlyFavs ? 'Filtrovány jen oblíbené (klikni pro všechny)' : 'Filtrovat jen oblíbené zbraně',
                  onclick: (e: MouseEvent) => {
                    e.stopPropagation();
                    this.toggleOnlyFavorites();
                  },
                },
                `⭐ ${favCount ? `(${favCount})` : 'Oblíbené'}`,
              ),
            ),
          ),
          h(
            'div',
            { class: 'weapon-grid' },
            this.droneAvailable() && !onlyFavs
              ? h(
                  'button',
                  {
                    type: 'button',
                    class: 'drone-item',
                    title: 'Průzkumný dron: prolétni mapu z pohledu první osoby. Nespotřebuje tah, 1 let za tah.',
                    onclick: () => {
                      this.pickerOpen = false;
                      this.renderLayer();
                      this.startDrone();
                    },
                  },
                  h('span', { class: 'ico' }, '🛸'),
                  h('span', {}, 'Dron'),
                  h('small', {}, 'průzkum'),
                )
              : null,
            ...displayWeapons.map((id, i) => {
              const w = WEAPONS[id];
              const ammo = ammoOf(tank.ammo, id);
              const isFav = this.isFavorite(id);
              const keyNum = i < 10 ? (i + 1) % 10 : null;
              const keyHint = keyNum !== null ? ` (klávesa ${keyNum})` : '';
              return h(
                'button',
                {
                  type: 'button',
                  class: `${id === this.aim.weapon ? 'sel ' : ''}${isFav ? 'is-fav' : ''}`,
                  disabled: ammo === 0,
                  title: `${w.description}${keyHint}`,
                  onclick: () => {
                    this.setAim({ weapon: id });
                    this.pickerOpen = false;
                    this.renderLayer();
                  },
                },
                h(
                  'span',
                  {
                    role: 'button',
                    tabIndex: 0,
                    class: `weapon-fav-btn ${isFav ? 'active' : ''}`,
                    title: isFav ? 'Odebrat z oblíbených' : 'Přidat mezi oblíbené',
                    onclick: (e: MouseEvent) => {
                      e.stopPropagation();
                      this.toggleFavorite(id);
                    },
                  },
                  isFav ? '★' : '☆',
                ),
                h('span', { class: 'ico' }, w.icon),
                h('span', {}, w.name),
                h('small', {}, ammo < 0 ? '∞' : `× ${ammo}`),
              );
            }),
            onlyFavs && displayWeapons.length === 0
              ? h(
                  'div',
                  { class: 'weapon-empty-favs' },
                  'Zatím nemáš vybrané žádné oblíbené zbraně. Klikni na hvězdičku ☆ u zbraní, které chceš přidat.',
                )
              : null,
          ),
        ),
      );
    }
    if (this.menuOpen) {
      const me = this.myTank();
      const canSurrender = !this.training && this.state.status === 'playing' && me?.alive;
      const fsSupported = !!document.documentElement.requestFullscreen;
      nodes.push(
        h(
          'div',
          { class: 'menu' },
          h(
            'button',
            {
              onclick: () => {
                this.cam.zoom = this.cam.fitZoom();
                this.cam.clamp();
                this.toggleMenu();
              },
            },
            '🔍 Zobrazit celou mapu',
          ),
          this.view3d
            ? h(
                'button',
                {
                  onclick: () => {
                    const v = this.view3d;
                    if (!v) return;
                    const next = QUALITY_ORDER[(QUALITY_ORDER.indexOf(v.level) + 1) % QUALITY_ORDER.length];
                    v.setQuality(next);
                    saveQuality(next);
                    this.renderLayer();
                  },
                },
                `🎨 Kvalita 3D: ${QUALITY_LABEL[this.view3d.level]}`,
              )
            : null,
          h(
            'button',
            {
              onclick: () => {
                const modes = ['auto', 'surround', 'stereo'] as const;
                const next = modes[(modes.indexOf(sound.engine.output) + 1) % modes.length];
                sound.setOutput(next);
                this.renderLayer();
              },
            },
            `🎧 Zvuk: ${{ auto: 'automaticky', surround: '5.1', stereo: 'stereo' }[sound.engine.output]}${sound.engine.surroundActive ? ' (hraje 5.1)' : ''}`,
          ),
          h(
            'label',
            { class: 'menu-volume' },
            '🔉',
            h('input', {
              type: 'range',
              min: 0,
              max: 1,
              step: 0.05,
              value: String(sound.engine.volume),
              'aria-label': 'Hlasitost',
              oninput: (e: Event) => sound.setVolume(Number((e.target as HTMLInputElement).value)),
            }),
          ),
          fsSupported
            ? h(
                'button',
                {
                  onclick: () => {
                    if (document.fullscreenElement) document.exitFullscreen();
                    else document.documentElement.requestFullscreen().catch(() => {});
                    this.toggleMenu();
                  },
                },
                '⛶ Celá obrazovka',
              )
            : null,
          this.training
            ? null
            : h(
                'button',
                {
                  onclick: () => {
                    toast(`Kód hry: ${this.view.code}`);
                    this.toggleMenu();
                  },
                },
                `# Kód hry: ${this.view.code}`,
              ),
          canSurrender
            ? h(
                'button',
                {
                  class: 'danger',
                  onclick: async () => {
                    this.toggleMenu();
                    if (!confirm('Opravdu se vzdát?')) return;
                    try {
                      await api.surrender(this.view.id);
                    } catch (e) {
                      toast((e as Error).message, true);
                    }
                  },
                },
                '🏳 Vzdát se',
              )
            : null,
          h('button', { onclick: () => go('/') }, this.training ? '← Ukončit trénink' : '← Do lobby'),
        ),
      );
    }
    this.nextRoundEl = null;
    if (this.mode === 'finished' && !this.overlayDismissed && this.view.tournament) {
      nodes.push(this.tournamentOverlay(this.view.tournament));
    } else if (this.mode === 'finished' && !this.overlayDismissed) {
      const s = this.state;
      const won = s.winner === this.me;
      const w = s.tanks.find((t) => t.id === s.winner);
      const isPlayer = s.tanks.some((t) => t.id === this.me);
      nodes.push(
        h(
          'div',
          { class: 'overlay' },
          h(
            'div',
            { class: 'card' },
            h('h2', {}, won ? '🏆 Vítězství!' : w ? `Vyhrál ${w.name}` : 'Remíza'),
            h('p', { class: 'muted', style: { margin: '0' } }, won ? 'Bojiště je tvoje.' : 'Příště to vyjde.'),
            this.summaryNode(),
            isPlayer
              ? h(
                  'button',
                  {
                    class: 'primary',
                    onclick: async () => {
                      try {
                        const { game } = await api.rematch(this.view.id);
                        go(`/game/${game.id}`);
                      } catch (e) {
                        toast((e as Error).message, true);
                      }
                    },
                  },
                  this.view.rematchId ? 'Připojit se k odvetě' : 'Odveta',
                )
              : null,
            h(
              'div',
              { class: 'row', style: { justifyContent: 'center' } },
              h(
                'button',
                {
                  onclick: () => {
                    this.overlayDismissed = true;
                    this.renderLayer();
                  },
                },
                'Prohlédnout mapu',
              ),
              h('button', { onclick: () => go('/') }, 'Lobby'),
            ),
          ),
        ),
      );
    }
    this.layer.replaceChildren(...nodes);
    this.tickNextRound();
  }

  /** Závěrečné okno kola turnaje: pořadí, odpočet do dalšího kola / vítěz turnaje. */
  private tournamentOverlay(t: TournamentInfo): HTMLElement {
    const s = this.state;
    const w = s.tanks.find((x) => x.id === s.winner);
    const tw = t.standings.find((x) => x.id === t.winnerId);
    const isPlayer = s.tanks.some((x) => x.id === this.me);
    const colorOf = (id: string) => s.tanks.find((x) => x.id === id)?.color;
    const title = t.finished
      ? t.winnerId === this.me
        ? '🏆 Vyhrál jsi turnaj!'
        : tw
          ? `🏆 Turnaj vyhrál ${tw.name}`
          : 'Turnaj skončil remízou'
      : `Kolo ${t.round}/${t.rounds}: ${s.winner === this.me ? 'vyhrál jsi!' : w ? `vyhrál ${w.name}` : 'remíza'}`;
    let action: Node | null = null;
    if (!t.finished && this.view.rematchId) {
      this.nextRoundEl = h('span', {});
      const next = this.view.rematchId;
      action = h('button', { class: 'primary', onclick: () => go(`/game/${next}`) }, this.nextRoundEl);
    } else if (!t.finished) {
      action = h('p', { class: 'muted small', style: { margin: '0' } }, 'Připravuji další kolo…');
    } else if (isPlayer) {
      action = h(
        'button',
        {
          class: 'primary',
          onclick: async () => {
            try {
              const { game } = await api.rematch(this.view.id);
              go(`/game/${game.id}`);
            } catch (e) {
              toast((e as Error).message, true);
            }
          },
        },
        this.view.rematchId ? 'Připojit se k novému turnaji' : 'Nový turnaj',
      );
    }
    return h(
      'div',
      { class: 'overlay' },
      h(
        'div',
        { class: 'card' },
        h('h2', {}, title),
        h('p', { class: 'muted', style: { margin: '0' } }, t.finished ? 'Celkové pořadí turnaje' : 'Průběžné pořadí turnaje'),
        standingsTable(t, colorOf, this.me),
        h('details', {}, h('summary', { class: 'muted small' }, `Statistiky ${t.round}. kola`), this.summaryNode()),
        action,
        h(
          'div',
          { class: 'row', style: { justifyContent: 'center' } },
          h(
            'button',
            {
              onclick: () => {
                this.overlayDismissed = true;
                this.renderLayer();
              },
            },
            'Prohlédnout mapu',
          ),
          h('button', { onclick: () => go('/') }, 'Lobby'),
        ),
      ),
    );
  }

  /** Aktualizuje odpočet do dalšího kola; po jeho konci (+ rezerva na start serveru) přejde do nové hry. */
  private tickNextRound() {
    const t = this.view.tournament;
    if (!t || t.finished || !t.nextStartsAt || !this.view.rematchId || this.state.status !== 'finished') return;
    const left = t.nextStartsAt - serverNow();
    if (this.nextRoundEl) this.nextRoundEl.textContent = left > 0 ? `Další kolo za ${Math.ceil(left / 1000)} s` : 'Do dalšího kola ▸';
    if (left < -1500 && !this.playback && !this.queue.length) go(`/game/${this.view.rematchId}`);
  }
}

/** Anemoskop postavíme deterministicky ze seedu tam, kde je nejdál od všech tanků. */
function pickWindsockX(state: GameState): number {
  const rng = mulberry32(hashInts(state.seed, 0xa11e));
  let best = WORLD_W / 2;
  let bestDist = -1;
  for (let i = 0; i < 16; i++) {
    const x = Math.floor(90 + rng() * (WORLD_W - 180));
    const d = Math.min(...state.tanks.map((t) => Math.abs(t.x - x)));
    if (d > bestDist) {
      bestDist = d;
      best = x;
    }
  }
  return best;
}
